
<template>
  <div>
    <div class="form-group">
      <label for="timezone">Select Timezone</label>

      <select v-model="selectedTimezone" class="timezone-select" @change="refreshGraph">
        <option disabled value="">Please select a timezone</option>

        <option
            v-for="(tz, index) in timezones"
            :key="index"
            :value="tz"
        >
          {{ tz }}
        </option>
      </select>
    </div>
    <v-row>
      <v-col
          cols="6"
          sm="6"
      >
        <v-card
            class="mx-auto"
            tile
            v-if="hidePast30dGraph === true"
        >
          <v-card-text>
            <div class="canvas-spinner">
              <v-progress-circular
                  v-if="currentDayDataLoading"
                  indeterminate
                  color="accent"
                  size="20"
              >
              </v-progress-circular>
            </div>
            <canvas id="thirtyday-activity"></canvas>
          </v-card-text>
        </v-card>
        <v-card v-else>
          <v-card-text>
            <div class="canvas-spinner">
             No Message Found in past 30 days
            </div>
          </v-card-text>

        </v-card>
      </v-col>
      <v-col
          cols="6"
          sm="6"
      >
        <v-card
            class="mx-auto"
            tile
        >
          <v-card-text>
            <div class="canvas-spinner">
              <v-progress-circular
                  v-if="loading"
                  indeterminate
                  color="accent"
                  size="20"
              >
              </v-progress-circular>
            </div>
            <canvas id="average-time"></canvas>
          </v-card-text>
        </v-card>
      </v-col>

    </v-row>
  </div>
</template>

<script>
import Cookies from "js-cookie";
import axios from "axios";
import Chart from 'chart.js/auto'
import { TZDate, tzOffset } from "@date-fns/tz";

export default {
  name: 'UserActivityAnalysis',
  props: {
    userName: {
      type: String,
      required: true
    },
    siteName: {
      type: String,
      required: true
    },
    timezones: {
      type: Array,
      required: true
    },
  },
  data() {
    return {
      past30DayMessagePostTimeGraph: null,
      overallMessagePostTimeGraph: null,
      selectedTimezone: 'UTC+00:00 UTC',
      thirtyDayData: [],
      overallPostedTimeData: [],
      docCount: [],
      dateLabel: [],
      avgDocCount: [],
      avgDateLabel: [],
      loading: false,
      currentDayDataLoading: false,
      hidePast30dGraph: true,
      hideOverallGraph: true
    }
  },
  mounted() {
    this.get_user_post_activity_30d_timeline_data()
    this.get_user_post_average_time()
  },
  methods: {
    // This just shifts the data around based on tzoffset since we
    // have all the data we need anyway
    shiftData(data) {
      let mydate = new Date()
      let tzoffset = tzOffset(this.selectedTimezone, mydate)
      let hourModifier = tzoffset / 60
      let newData = structuredClone(data)
      if (hourModifier !== 0) {
        for (let i = 0; i < Math.abs(hourModifier); i++) {
          if (hourModifier > 0) {
            newData.unshift(newData.pop())
          } else {
            newData.push(newData.shift())
          }
        }
      }
      return newData
    },
    refreshGraph() {
      // does this.dateLabel exist here?
      // It might exist, but be an empty array, so just check for that.
      if (this.dateLabel && this.dateLabel.length > 0) {
        let new_30day_data = this.shiftData(this.dateLabel)
        this.past30DayMessagePostTimeGraph.data.datasets[0].data = new_30day_data
        this.past30DayMessagePostTimeGraph.update()
      }
      // I think this one always exists, but might just have zeroes
      let new_average_data = this.shiftData(this.avgDateLabel)
      this.overallMessagePostTimeGraph.data.datasets[0].data = new_average_data
      this.overallMessagePostTimeGraph.update()

    },
    get_user_post_activity_30d_timeline_data() {
      this.currentDayDataLoading = true
      const csrftoken = Cookies.get('csrftoken')
      const apiUrl = `/en/user/daily/post/timeline/${this.userName}/${this.siteName}/`
      axios.get(apiUrl, {
        'headers': {'X-CSRFToken': csrftoken}, params: {
          timezone: this.selectedTimezone
        }
      }).then(response => {
        this.thirtyDayData = response.data.data
        if (!this.thirtyDayData || this.thirtyDayData.length === 0) {
          this.hidePast30dGraph = false
          return
        }
        this.docCount = Object.keys(this.thirtyDayData)
        this.dateLabel = Object.values(this.thirtyDayData)
        this.render30dChart(this.docCount, this.dateLabel)
        this.currentDayDataLoading = false
      })
    },
    get_user_post_average_time() {
      this.loading = true
      const csrftoken = Cookies.get('csrftoken')
      const apiUrl = `/en/user/post/timeline/${this.userName}/${this.siteName}/`
      axios.get(apiUrl, {
        'headers': {'X-CSRFToken': csrftoken}, params: {
          timezone: this.selectedTimezone
        }
      }).then(response => {
        this.overallPostedTimeData = response.data.data
        if (!this.overallPostedTimeData || this.overallPostedTimeData.length === 0) {
          this.hideOverallGraph = false
          return
        }
        this.avgDocCount = Object.keys(this.overallPostedTimeData)
        this.avgDateLabel = Object.values(this.overallPostedTimeData)
        this.renderAveragePostChart(this.avgDocCount, this.avgDateLabel)
        this.loading = false
        this.hideOverallGraph = true
      })
    },
    render30dChart(date_label, doc_count) {
      const ctx = document.getElementById('thirtyday-activity');
      this.past30DayMessagePostTimeGraph = new Chart(ctx, {
        type: 'line',
        data: {
          labels: date_label,
          datasets: [
            {
              label: "Number of Messages",
              data: doc_count,
              fill: true,
              borderColor: 'rgb(194, 44, 63)',
              pointStyle: 'circle',
              radius: 5,
              tension: 0.1,
            }
          ]
        },
        options: {
          responsive: true,
          plugins: {
            title: {
              display: true,
              text: 'Message Timeline For Past 30 days'
            },
            legend: {
              labels: {
                usePointStyle: true,
              }
            },
          },
          scales: {
            x: {
              beginAtZero: true,
              ticks: {
                callback: (value) => {
                  if (value < 10) {
                    return '0' + value + ':00'
                  }
                  return value + ':00'
                }
              },
              title: {
                display: true,
                text: 'Time',
                font: {
                  weight: 'bold'
                }
              }
            },
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Number of Messages',
                font: {
                  weight: 'bold'
                }
              }
            }
          }
        }
      });
    },
    renderAveragePostChart(date_label, doc_count) {
      const ctx = document.getElementById('average-time');
      this.overallMessagePostTimeGraph = new Chart(ctx, {
        type: 'line',
        data: {
          labels: date_label,
          datasets: [
            {
              label: "Number of Messages",
              data: doc_count,
              fill: true,
              borderColor: 'rgb(44,119,194)',
              tension: 0.1,
              pointStyle: 'circle',
              radius: 5,
            }
          ]
        },
        options: {
          responsive: true,
          plugins: {
            title: {
              display: true,
              text: 'Overall Message Timeline Summary'
            },
            legend: {
              labels: {
                usePointStyle: true,
              }
            },
          },
          scales: {
            x: {
              beginAtZero: true,
              ticks: {
                callback: function(value) {
                  // my kingdom for sprintf
                  if (value < 10) {
                    return '0' + value + ':00'
                  }
                  return value + ':00'
                }
              },
              title: {
                display: true,
                text: 'Time',
                font: {
                  weight: 'bold'
                }
              }
            },
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Number of Messages',
                font: {
                  weight: 'bold'
                }
              }
            }
          }
        }
      });
    }
  }
}
</script>

<style scoped>
.canvas-spinner {
  text-align: center;
}

.timezone-select {
  padding: 10px 12px;
  font-size: 14px;
  background-color: #fff;
  cursor: pointer;
  transition: border 0.3s ease, box-shadow 0.3s ease;
  width: 50%;
}

.timezone-select:focus {
  border-color: #c22c3f;
  outline: none;
  box-shadow: 0 0 0 2px #c22c3f;;
}
</style>